<template>
    <LayoutWithContainer :small="true">
        <div v-if="$wait.is('fetching preview resource')" class="loading">
            <Spinner :height="40" :width="40"/>
        </div>
        <template v-else>
            <template slot="header">
                <ContentHeader
                    :title="sectorTitle"
                    back-link-title="Go Back"
                    :back-link="`/sectors/${sectorId}`"
                    :bordered="false"
                />
            </template>
            <CreatePost
                ref="createPostForm"
                :is-editing="true"
                :sector-id="sectorId"
                :default-values="formDataDefaultValues"
                @form-submit="handleFormSubmit"
            />
            <button type="button" class="delete-button" @click.prevent.stop="handleDeleteClicked">Delete Post</button>
        </template>
    </LayoutWithContainer>
</template>
<script>
import unionBy from 'lodash.unionby';
import API from '@api';
import LayoutWithContainer from '@layouts/LayoutWithContainer';
import CreatePost from '@components/Sector/SingleView/CreatePost';
import ResourcePreview from '@components/ResourcePreview';
import Spinner from '@components/Spinner';
import ContentHeader from '@components/Header/ContentHeader.vue';
import Sectors from '../../../sectors';
import merge from 'lodash.merge';

export default {
    name: 'SectorPostEdit',
    components: {
        ContentHeader,
        Spinner,
        CreatePost,
        LayoutWithContainer,
    },
    mixins: [ResourcePreview],
    props: {
        sectorId: {
            type: Number,
            required: true,
        },
        id: {
            type: Number,
            required: true,
        },
    },
    data: () => ({
        resource: null,
    }),
    computed: {
        formDataDefaultValues: function() {
            if(!this.resource) {
                return {};
            }

            const relatedItems = unionBy(
                [],
                (this.resource.opportunities ?? []).map((item) => ({ ...item, type: 'Opportunity' })),
                (this.resource.madds ?? []).map((item) => ({ ...item, type: 'MADD' })),
                'id',
            )
                .map((item) => ({
                    'id': item.id,
                    'companyName': item.companyName ?? null,
                    'projectName': item.projectName ?? null,
                    'type': `App\\Models\\${ item.type }`,
                    'shortType': item.type,
                    'sectors': item.sectors ?? [],
                    'country': item.country ?? null,
                }));

            return {
                post_content: this.resource.post_content,
                related_item: relatedItems && relatedItems.length ? relatedItems[0] : null,
                files: this.resource.files.map((mediaItem) => mediaItem.uuid),
                image: (this.resource.media ?? []).map((mediaItem) => mediaItem.uuid)[0] ?? null,
                sectors: this.resource.sectors ?? [],
                sectorOptions: this.resource.sector_options ?? null,
            }
        },
        sectorTitle: function(){
            if(Sectors){
                const foundSector = Sectors.find((sector) => sector.id === this.sectorId);

                if(foundSector){
                    return foundSector.name;
                }
            }

            return null;
        }
    },
    methods: {
        async handleFormSubmit(formData) {
            try {
                this.$refs.createPostForm.startWait();

                formData = merge({},
                    formData,
                    { sectors: [...formData.sectors, this.id] }
                )

                await API.updateSectorPost(this.id, formData);

                this.$refs.createPostForm.endWait();
                this.$router.push({
                    name: 'sector-single-view',
                    params: {
                        id: this.sectorId,
                    },
                });
            } catch(e) {
                console.warn(e.message);
            } finally {
                this.$refs.createPostForm.endWait();
            }
        },
        async fetchResource() {
            try {
                const data = await API.getSectorPost(this.id);

                if(!data.can || !data.can.edit) {
                    this.$router.push({
                        name: 'sector-post-view',
                        params: {
                            sector_id: this.sectorId,
                            post_id: this.id,
                        },
                    })
                }

                return data;
            } catch(err) {
                console.warn({err});
                this.redirectToSector();
            }
        },
        handleDeleteClicked: function() {
            this.$modal.show('dialog', {
                title: 'Are you sure?',
                text: 'Clicking DELETE will remove this post.',
                buttons: [
                    {
                        class: 'btn btn--danger',
                        title: this.$wait.is('deleting') ? 'Deleting...' : 'DELETE',
                        handler: this.handleDelete.bind(this),
                    },
                ],
            });
        },
        handleDelete: async function() {
            const removePostId = this.resource.id;

            try {
                this.$wait.start('deleting');
                await API.deleteSectorPost(removePostId);
            } catch(err) {
                console.warn(err);
            } finally {
                this.$modal.hide('dialog');
                this.$wait.end('deleting');
                this.redirectToSector();
            }
        },
        redirectToSector: function() {
            this.$router.push({
                name: 'sector-single-view',
                params: { sector_id: this.sectorId },
            })
        }
    },

}
</script>
<style scoped lang="scss">
@import '~@/theme/_variables.scss';

.delete-button {
    appearance: none;
    -webkit-appearance: none;
    background-color: transparent;
    border: none;
    color: $accent-header;
    padding: .5rem .7rem;
    width: 8.8rem;
    margin: 2rem auto 0;
    display: block;
    font-family: "Axiforma", sans-serif;
    cursor: pointer;
}
</style>
